import { Col, Modal, Pagination, Row, Space, Spin, Table, Tag } from 'antd';
import React, { useEffect, useState } from 'react';
import { EyeOutlined, DownloadOutlined } from '@ant-design/icons';
import { TripsFilter } from './TripsFilter';
import { useSortedTripQuery } from '../hooks/useSortedTripQuery';
import { useCurrentTrip } from '../hooks/useCurrentTrip';
import TripsViewModal from './TripsViewModal';
import { useGetInvoiceByTripIdQuery } from '../../../GraphQL/hooks/useGetInvoiceByTripIdQuery';
import { convertDateToFormat } from '../../../shared/Utils';

const TripTable = () => {
    const { sortedTrips: trips, refetch, loading, ...pagination } = useSortedTripQuery();
    const [isViewModalVisible, setViewModalVisible] = useState(false);
    const { setCurrentTrip } = useCurrentTrip();

    const [tripId, setTripId] = useState(null);

    const { loading: loadingInvoice, data: invoiceData } = useGetInvoiceByTripIdQuery(tripId);

    useEffect(() => {
        if (tripId && invoiceData && 'invoiceAttachment' in invoiceData && invoiceData.invoiceAttachment) {
            const { url } = invoiceData.invoiceAttachment;
            if (url) {
                window.open(url, '_blank');
                setTripId(null);
            }
        }
    }, [invoiceData, tripId]);

    const handleViewTrip = (rider) => {
        setCurrentTrip(rider);
        setViewModalVisible(true);
    };

    const handleViewInvoice = (trip) => {
        if (trip.status === 'Closed') {
            Modal.warning({
                title: 'Invoice not available',
                content: 'This trip is still in progress. The invoice will be available once the trip is completed.',
                okText: 'Ok',
            });
            return;
        }
        setTripId(trip._id);
        setViewModalVisible(false);
    };

    const columns = [
        {
            title: 'ID',
            dataIndex: 'logicalId',
            key: 'logicalId',
            sorter: (a, b) => a.logicalId - b.logicalId,
            width: 80,
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            width: 100,
        },
        {
            title: 'Start date',
            dataIndex: 'startDate',
            key: 'startDate',
            width: 150,
            sorter: (a, b) => a.startDate - b.startDate,
            render: (text, record) => {
                return convertDateToFormat(record.startDate, 'DD-MM-YYYY, HH:mm');
            },
        },
        {
            title: 'End date',
            dataIndex: 'endDate',
            key: 'endDate',
            width: 150,
            sorter: (a, b) => a.endDate - b.endDate,
            render: (text, record) => {
                if (record.status === 'Running' || !record.endDate) {
                    return (
                        <Tag color="#faad14" style={{ color: '#fff' }}>
                            In progress
                        </Tag>
                    );
                }
                // Otherwise, show the formatted date
                return convertDateToFormat(record.endDate, 'DD-MM-YYYY, HH:mm');
            },
        },
        {
            title: 'Rider name',
            dataIndex: 'riderId',
            key: 'riderId',
            width: 100,
            render: (_, trip) => trip.riderId?.fullName,
        },
        {
            title: 'Action',
            key: 'action',
            width: 100,
            render: (_, trip) => (
                <Space size="middle">
                    <a href="#" onClick={() => handleViewTrip(trip)}>
                        <EyeOutlined />
                    </a>
                    <a href="#" onClick={() => handleViewInvoice(trip)}>
                        <DownloadOutlined />
                    </a>
                </Space>
            ),
        },
    ];

    const Paginator = () => (
        <Row justify={'center'}>
            <Col>
                <Pagination
                    size="default"
                    total={pagination.totalCount}
                    pageSize={pagination.pageSize}
                    current={pagination.page}
                    showTotal={(total) => `Total Trips ${total}`}
                    onChange={(newPage) => pagination.setPage(newPage)}
                    showQuickJumper={true}
                    showSizeChanger={false}
                    showLessItems={false}
                />
            </Col>
        </Row>
    );

    return (
        <>
            {isViewModalVisible && <TripsViewModal isOpen={isViewModalVisible} onRequestClose={setViewModalVisible} />}

            <div style={{ flex: '1 1 auto' }}>
                <Row gutter={[16, 6]} style={{ paddingBottom: 10, paddingTop: 20 }}>
                    <TripsFilter />
                </Row>
                <Row gutter={16} style={{ paddingBottom: 10, paddingTop: 10 }}>
                    <Col xs={24}>
                        <Spin spinning={loading || loadingInvoice}>
                            <Table
                                dataSource={trips}
                                columns={columns}
                                pagination={false}
                                rowKey={() => Math.random().toString()}
                                scroll={{ x: 1500 }}
                            ></Table>
                        </Spin>
                    </Col>
                </Row>
                <Paginator />
            </div>
        </>
    );
};

export default TripTable;
